import { Component, inject, input, OnChanges, OnDestroy, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { faCodeBranch } from '@fortawesome/pro-regular-svg-icons';

import { plainToInstance } from 'class-transformer';
import { Subscription } from 'rxjs';
import { AddCondition } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionEditor } from '../../models/question-editor.model';
import { SurveyConditionalsService } from '../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../services/survey-editor.service';

@Component({
  selector: 'empathy-conditional-logic',
  templateUrl: 'conditional-logic.component.html',
  styleUrls: ['./conditional-logic.component.scss', './_conditional-logic-theme.component.scss']
})
export class ConditionalLogicComponent implements OnChanges, OnDestroy {
  activeQuestion = input<FormGroup>();
  activeHiddenQuestion = input<FormGroup>();
  activeHiddenOption = input<FormGroup>();
  activeOption = input<FormGroup>();
  activeCard = input<FormGroup>();

  previousQuestions$ = signal<Question[]>([]);
  isEditing$ = signal<boolean>(false);

  faCodeBranch = faCodeBranch;

  surveyEditorService = inject(SurveyEditorService);

  private subscriptions: Subscription[] = [];
  private surveyConditionalsService = inject(SurveyConditionalsService);

  ngOnChanges(): void {
    if (!this.activeQuestion() && !this.activeHiddenQuestion()) {
      return;
    }

    this.isEditing$.set(false);

    const sourceQuestions = plainToInstance(
      QuestionEditor,
      this.surveyEditorService
        .surveyEditor$()
        .sources.all.flatMap((sourceCtrl) => sourceCtrl.value.variables as QuestionEditor[])
        .filter((question) => question.variableRequiredType !== 'UNIQUE' && !!question.variableToken),
      { enableImplicitConversion: true }
    );

    let previousQuestions: Question[] = [];
    if (this.activeHiddenQuestion()) {
      previousQuestions = this.surveyEditorService.surveyEditor$().previousConditionSelectableQuestionsForHiddenQuestions();
    } else if (this.activeQuestion()) {
      previousQuestions = this.surveyEditorService.surveyEditor$().previousConditionSelectableQuestionsWithBlockAllowed();
    }

    this.previousQuestions$.set([...sourceQuestions, ...previousQuestions]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onSave(newCondition: AddCondition): void {
    if (this.activeOption()) {
      this.surveyConditionalsService.addConditionToActiveQuestionOption(newCondition);
    } else if (this.activeCard()) {
      this.surveyConditionalsService.addConditionToActiveQuestionCard(newCondition);
    } else if (this.activeHiddenOption()) {
      this.surveyConditionalsService.addConditionToActiveHiddenQuestionOption(newCondition);
    } else {
      this.surveyConditionalsService.addConditionToActiveQuestion(newCondition);
    }

    this.isEditing$.set(false);
    this.surveyConditionalsService.conditionForm$().reset();
    this.surveyConditionalsService.questionSelected$.set(null);
  }

  onRemoveCondition(index: number): void {
    if (this.activeOption()) {
      this.surveyConditionalsService.removeConditionFromActiveOption(index);
    } else if (this.activeCard()) {
      this.surveyConditionalsService.removeConditionFromActiveCard(index);
    } else if (this.activeHiddenOption()) {
      this.surveyConditionalsService.removeConditionFromActiveHiddenOption(index);
    } else {
      this.surveyConditionalsService.removeConditionFromActiveQuestion(index);
    }
  }
}
